import { useEffect, useState } from "react";
import { DollarSign, TrendingUp, Crown, FileDown, Wallet } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PortalShell } from "@/components/PortalShell";
import { Button } from "@/components/ui/button";

const STATUS_PILL = {
  pending:   { bg: "bg-amber-100",   text: "text-amber-800",   label: "Pending" },
  approved:  { bg: "bg-cyan-100",    text: "text-cyan-800",    label: "Approved" },
  paid:      { bg: "bg-emerald-100", text: "text-emerald-800", label: "Paid" },
  rejected:  { bg: "bg-rose-100",    text: "text-rose-800",    label: "Rejected" },
  cancelled: { bg: "bg-slate-100",   text: "text-slate-700",   label: "Cancelled" },
  flagged:   { bg: "bg-orange-100",  text: "text-orange-800",  label: "Flagged" },
};
const KIND_LABEL = { water_test: "Water test", membership: "Membership", property_transfer: "Transfer", service: "Service" };
const fmt = (n) => `$${(n || 0).toFixed(2)}`;

export default function TradeEarnings() {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    api.get("/trade/earnings").then(({ data }) => setSummary(data)).catch(() => {});
    api.get("/trade/commissions").then(({ data }) => setRows(data)).catch(() => {});
  }, []);

  const dlStatement = async () => {
    const token = localStorage.getItem("scwt_token");
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/trade/earnings/statement.pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) return toast.error("No statement available");
    const blob = await r.blob();
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `my-earnings-statement.pdf`; a.click();
  };

  if (!summary) return <PortalShell><div className="p-8 text-center text-slate-500">Loading…</div></PortalShell>;

  if (!summary.partner) {
    return (
      <PortalShell>
        <div data-testid="trade-earnings-page" className="max-w-3xl mx-auto px-4 py-16 text-center">
          <Wallet className="w-12 h-12 text-slate-300 mx-auto" />
          <h1 className="font-display text-2xl font-bold text-slate-900 mt-4">No earnings yet</h1>
          <p className="text-slate-500 mt-2 text-sm">You'll see commissions here once a customer arrives via your shared certificates and pays for testing or joins the AWARE Water Health Record.</p>
        </div>
      </PortalShell>
    );
  }

  return (
    <PortalShell>
      <div data-testid="trade-earnings-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-emerald-600">My earnings</div>
            <h1 className="font-display mt-2 text-3xl font-bold text-slate-900">Commissions & earnings</h1>
            <p className="text-slate-500 mt-1 text-sm">Earned from properties you've referred via certificate shares. Paid out manually by Aware after admin review.</p>
          </div>
          <Button data-testid="trade-statement-btn" onClick={dlStatement} className="rounded-full bg-slate-900 hover:bg-cyan-700 text-white">
            <FileDown className="w-4 h-4 mr-1.5" /> Download statement
          </Button>
        </div>

        {/* KPI cards */}
        <div className="mt-7 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="earnings-metrics">
          <Card label="Pending" value={fmt(summary.pending_earnings)} color="amber" />
          <Card label="Approved" value={fmt(summary.approved_earnings)} color="cyan" />
          <Card label="Paid" value={fmt(summary.paid_earnings)} color="emerald" />
          <Card label="Lifetime" value={fmt(summary.lifetime_earnings)} color="slate" />
        </div>

        <div className="mt-5 rounded-3xl bg-gradient-to-br from-slate-900 to-cyan-900 text-white p-7 grid sm:grid-cols-3 gap-5">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-300">Outstanding balance</div>
            <div className="font-display text-4xl font-bold mt-1">{fmt(summary.outstanding_balance)}</div>
            <div className="text-xs text-slate-300 mt-0.5">Pending + approved</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-300">Revenue generated</div>
            <div className="font-display text-4xl font-bold mt-1">{fmt(summary.revenue_generated)}</div>
            <div className="text-xs text-slate-300 mt-0.5">For Aware through your referrals</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-300">Avg / referral</div>
            <div className="font-display text-4xl font-bold mt-1">{fmt(summary.average_revenue_per_referral)}</div>
            <div className="text-xs text-slate-300 mt-0.5">{summary.referral_count} referral{summary.referral_count !== 1 ? "s" : ""} total</div>
          </div>
        </div>

        {/* Commission history */}
        <div className="mt-7">
          <h2 className="font-display text-xl font-bold text-slate-900 mb-3">Commission history</h2>
          <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wider font-bold text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-3">Date</th>
                    <th className="text-left px-4 py-3">Property</th>
                    <th className="text-left px-4 py-3">Type</th>
                    <th className="text-right px-4 py-3">Revenue</th>
                    <th className="text-right px-4 py-3">Rate</th>
                    <th className="text-right px-4 py-3">Earned</th>
                    <th className="text-left px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr><td colSpan="7" className="text-center py-10 text-slate-500">No commissions yet.</td></tr>
                  ) : rows.map((c) => {
                    const pill = STATUS_PILL[c.status] || STATUS_PILL.pending;
                    return (
                      <tr key={c.id} className="border-t border-slate-100">
                        <td className="px-4 py-3 text-xs text-slate-600">{c.earned_at?.slice(0, 10)}</td>
                        <td className="px-4 py-3 text-slate-700">{c.property_address || "—"}</td>
                        <td className="px-4 py-3 text-slate-700">{KIND_LABEL[c.revenue_kind] || c.revenue_kind}</td>
                        <td className="px-4 py-3 text-right font-mono">{fmt(c.revenue_amount)}</td>
                        <td className="px-4 py-3 text-right text-slate-600">{(c.commission_rate * 100).toFixed(0)}%</td>
                        <td className="px-4 py-3 text-right font-mono font-bold text-emerald-700">{fmt(c.commission_amount)}</td>
                        <td className="px-4 py-3"><span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${pill.bg} ${pill.text}`}>{pill.label}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </PortalShell>
  );
}

function Card({ label, value, color }) {
  const styles = {
    amber: "border-amber-200",
    cyan: "border-cyan-200",
    emerald: "border-emerald-200",
    slate: "border-slate-200",
  }[color];
  return (
    <div className={`rounded-3xl bg-white border ${styles} p-5`}>
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400">{label}</div>
      <div className="font-display text-3xl font-bold text-slate-900 mt-1">{value}</div>
    </div>
  );
}
